import * as React from 'react';
import { Button, SecondaryButton } from '@/components/display/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons';
import { AiChatModal } from '@/features/ai/ai-chat-modal';
import {
  TextBubble,
  SystemNoteLine,
  WorkspaceEmptyState,
  WorkspaceSuggestions,
  buildChatSegments,
  pendingWorkspaceQuestion
} from '@/features/ai/ai-chat-primitives';
import { LineItemPreviewCard } from './line-item-preview-card';
import { LineItemMutateCard } from './line-item-mutate-card';
import { LineItemBatchUpdateCard } from './line-item-batch-update-card';
import { externalCommitOutcome } from '@/features/ai/shared/commit-card-frame';
import { TaskChooser, type TaskOption } from '@/features/ai/shared/task-chooser';
import {
  ConfigurationCard,
  isSelectionComplete,
  type CardSelection,
  type InferredFromZohoFields
} from './configuration-card';
import { CampaignContextCard } from './campaign-context-card';
import {
  buildProposePrompt,
  createdDraftsBudget,
  extractWorkspaceData,
  prefillDrafts,
  type FinalizedDrafts
} from './line-item-workspace-data';
import { ZohoNotesCard } from '@/features/ai/shared/zoho-notes-card';
import { useChannelRatesContext } from '@/features/strategy/channel-rates-context';
import type { Channel } from 'shared/src/domain/line-item/line-item-channels';
import { resolveZohoDefaults } from 'shared/src/domain/line-item/resolve-zoho-defaults';
import type {
  LineItemChatMessage,
  LineItemChatTools,
  CampaignContextData,
  BravoWriteResult,
  UpdateLineItemsToolInput,
  DeleteLineItemToolInput,
  PromoteProposalToolInput,
  StepUpdateData,
  PrefillLineItemsData
} from 'shared/src/api/ai-line-item-chat-types';
import type { WorkspaceHelpers } from '@/features/ai/ai-chat-modal';

type Props = {
  proposalId: string;
  campaignName?: string;
  // Escape hatch: let a batch-update row close the modal and open the full line-item form for
  // that row instead of editing pacing inline.
  onEditLineItem?: (lineItemId: string) => void;
};

const EMPTY_SELECTION: CardSelection = { platformIds: [] };

export function AiGenerateLineItemsButton({ proposalId, campaignName, onEditLineItem }: Props) {
  const [open, setOpen] = React.useState(false);
  // One or more line-item drafts being configured. The create flow lets the user build several and
  // create them in one batch; a single draft is the common case.
  const [drafts, setDrafts] = React.useState<CardSelection[]>([EMPTY_SELECTION]);
  const [inferredFromZoho, setInferredFromZoho] = React.useState<InferredFromZohoFields>({});
  // null = task-selection landing; 'create' shows the configuration card(s); 'agent' = the user picked
  // a manage task or typed freeform, so the agent drives via tools.
  const [mode, setMode] = React.useState<'create' | 'agent' | null>(null);
  const zohoHydratedFor = React.useRef<string | null>(null);
  // The id of the last prefillLineItems data part already applied to the drafts (apply once per part).
  const prefillAppliedFor = React.useRef<string | null>(null);
  // Guards the create-finalize button against a double-click in the window before isBusy flips true.
  const creatingRef = React.useRef(false);
  // The drafts last sent for review and the remaining budget they were checked against. Keyed by
  // the drafts array itself, so any edit (a new array) drops it. Once those drafts are created the
  // live remaining budget falls to $0, and the panel must keep measuring them against the old one.
  const [finalized, setFinalized] = React.useState<FinalizedDrafts | null>(null);
  const { channels } = useChannelRatesContext();

  const modalTitle = campaignName ? `Assist Me · ${campaignName}` : 'Assist Me';

  React.useEffect(() => {
    if (!open) {
      setDrafts([EMPTY_SELECTION]);
      setInferredFromZoho({});
      setMode(null);
      zohoHydratedFor.current = null;
      prefillAppliedFor.current = null;
      creatingRef.current = false;
      setFinalized(null);
    }
  }, [open]);

  const renderChatContent = React.useCallback((message: LineItemChatMessage) => {
    // Chat shows conversation only (text + status notes). Actionable choices (presentOptions) render
    // in the workspace via WorkspaceSuggestions.
    const segments = buildChatSegments(message.parts);
    return segments.map((seg, si) => {
      if (seg.type === 'text') {
        return <TextBubble key={`text-${si}`} texts={seg.texts} />;
      }
      if (seg.type === 'system-note') {
        return <SystemNoteLine key={`sys-${si}`} text={seg.text} />;
      }
      return null;
    });
  }, []);

  const renderWorkspaceContent = React.useCallback(
    (allMessages: LineItemChatMessage[], helpers: WorkspaceHelpers) => {
      const {
        campaignContext,
        latestCreateCall,
        latestMutateCall,
        remainingBudget,
        platformNameById
      } = extractWorkspaceData(allMessages, channels);

      // Per-draft edit (only the first draft carries Zoho-inferred badges).
      const handleDraftChange = (index: number, next: CardSelection) => {
        setDrafts(prev => prev.map((d, i) => (i === index ? next : d)));
        if (index === 0) setInferredFromZoho(prev => clearOverriddenBadges(prev, drafts[0], next));
      };

      const addDraft = () => setDrafts(prev => [...prev, { platformIds: [] }]);
      const removeDraft = (index: number) => {
        setDrafts(prev => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
        // Zoho-inferred badges are bound to the first draft; if it's removed, the next draft shifts
        // into its place and must not inherit badges it never got from Zoho.
        if (index === 0) setInferredFromZoho({});
      };

      // The agent signals "use the configuration card" by advancing to the Configure step. Binding the
      // card's visibility to that signal (in addition to the chooser's explicit create pick) is the
      // harness guarantee: the assistant can't point at a card that isn't rendered — directing the user
      // to it REQUIRES emitting the step that shows it. Honor the step signal only when NOT in a manage
      // flow ('agent'), so a stray Configure step during edit/delete/promote can't pop the create cards.
      const agentConfiguring = latestStep(allMessages) === CONFIGURE_STEP;
      // The agent can pre-fill the configuration card(s) from the user's chat description; applying a
      // prefill enters create mode, so it also counts as a Configure signal.
      const latestPrefill = latestPrefillLineItems(allMessages);
      const inCreate =
        mode === 'create' || ((agentConfiguring || Boolean(latestPrefill)) && mode !== 'agent');

      // Any user message beyond the auto-sent initial one means the user has engaged (picked a task or
      // typed freeform) — hide the landing chooser once that happens.
      const userHasTyped = allMessages.some((m, i) => m.role === 'user' && i > 0);
      const showChooser = mode === null && !userHasTyped && !inCreate && Boolean(campaignContext);
      const pending = pendingWorkspaceQuestion(allMessages, helpers.isBusy);

      // Create-flow finalize: all drafts complete AND their budgets sum to the remaining budget within
      // the documented $0.01 tolerance (matches the route/skill prompt rule).
      const draftsComplete = drafts.every(d => isSelectionComplete(d, channels));
      const draftsTotal = drafts.reduce((sum, d) => sum + (d.price ?? 0), 0);
      const { draftsCreated, panelBudget } = createdDraftsBudget(
        finalized,
        drafts,
        latestCreateCall,
        remainingBudget
      );
      const budgetReconciles = Math.abs(draftsTotal - panelBudget) <= 0.01;
      const canCreate = draftsComplete && budgetReconciles && !helpers.isBusy && !draftsCreated;

      // Once a create has been dispatched, the button is disabled via isBusy — but isBusy flips
      // asynchronously, so guard the brief sync window against a double-click firing two finalizes.
      if (helpers.isBusy) creatingRef.current = false;
      const handleCreate = () => {
        if (!canCreate || creatingRef.current) return;
        creatingRef.current = true;
        try {
          helpers.sendMessage(buildProposePrompt(drafts, channels));
          setFinalized({
            drafts,
            budget: remainingBudget,
            priorCreateCallId: latestCreateCall?.toolCallId ?? null
          });
        } catch (err) {
          // If the prompt build throws (e.g. a prefilled id missing from the rate card), release
          // the double-click guard — otherwise the button stays enabled but permanently inert.
          creatingRef.current = false;
          throw err;
        }
      };

      const handleTaskSelect = (taskId: string) => {
        if (taskId === 'create') {
          // Seed a single draft pre-filled with the full remaining budget (the common case); the user
          // can split it across several by adding more.
          setDrafts([
            { platformIds: [], price: remainingBudget > 0 ? remainingBudget : undefined }
          ]);
          setMode('create');
          helpers.sendMessage('I want to create a new line item.');
          return;
        }
        setMode('agent');
        helpers.sendMessage(LINE_ITEM_TASK_SEEDS[taskId] ?? taskId);
      };

      if (
        !campaignContext &&
        !latestCreateCall &&
        !latestMutateCall &&
        drafts[0]?.channelId === undefined
      ) {
        return <WorkspaceEmptyState stepLabel="Waiting for AI to gather context" />;
      }

      // Focus takeover: while a commit card is pending the user's decision, it is the only
      // interactive surface — everything else dims and stops receiving pointer events.
      const createPending = latestCreateCall?.state === 'input-available';
      const mutatePending = latestMutateCall?.state === 'input-available';
      const commitPending = Boolean(createPending || mutatePending);
      const dimClass = commitPending ? 'pointer-events-none select-none opacity-40' : '';

      return (
        <div className="space-y-4">
          <div className={`space-y-4 ${dimClass}`}>
            {campaignContext ? <CampaignContextCard data={campaignContext} /> : null}
            {campaignContext ? <ZohoNotesCard notes={campaignContext.zoho_notes_module} /> : null}
            {showChooser && campaignContext ? (
              <TaskChooser
                options={lineItemTaskOptions(
                  campaignContext.existing_line_items.length,
                  remainingBudget
                )}
                onSelect={handleTaskSelect}
              />
            ) : null}
          </div>
          {latestPrefill ? (
            <PrefillHydrator
              prefill={latestPrefill}
              channels={channels}
              setDrafts={setDrafts}
              setMode={setMode}
              setInferredFromZoho={setInferredFromZoho}
              prefillAppliedFor={prefillAppliedFor}
            />
          ) : null}
          {inCreate && campaignContext && !latestPrefill ? (
            <ZohoDefaultsHydrator
              campaignContext={campaignContext}
              channels={channels}
              setDrafts={setDrafts}
              setInferredFromZoho={setInferredFromZoho}
              zohoHydratedFor={zohoHydratedFor}
            />
          ) : null}
          {inCreate && campaignContext ? (
            <div className={`space-y-3 ${dimClass}`}>
              {drafts.map((draft, i) => (
                <ConfigurationCard
                  key={`draft-${i}`}
                  channels={channels}
                  selection={draft}
                  remainingBudget={panelBudget}
                  isBusy={helpers.isBusy}
                  inferredFromZoho={i === 0 ? inferredFromZoho : undefined}
                  onChange={next => handleDraftChange(i, next)}
                  headerLabel={drafts.length > 1 ? `Line item ${i + 1}` : undefined}
                  onRemove={drafts.length > 1 ? () => removeDraft(i) : undefined}
                />
              ))}
              <button
                type="button"
                onClick={addDraft}
                className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                + Add another line item
              </button>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3">
                <span className={`text-xs ${budgetReconciles ? 'text-gray-500' : 'text-red-500'}`}>
                  Total ${draftsTotal.toLocaleString()} / ${panelBudget.toLocaleString()}
                  {draftsCreated
                    ? ' — created'
                    : budgetReconciles
                      ? ''
                      : ' — budgets must sum to the remaining budget'}
                </span>
                {/* Deliberately non-committal: this only sends the drafts to the assistant, which
                    responds with the commit card — the single place the write actually happens. */}
                <SecondaryButton size="compact" disabled={!canCreate} onClick={handleCreate}>
                  Review changes →
                </SecondaryButton>
              </div>
            </div>
          ) : null}
          {latestCreateCall ? (
            <div className={commitPending && !createPending ? dimClass : undefined}>
              <LineItemPreviewCard
                key={latestCreateCall.toolCallId}
                lineItems={latestCreateCall.lineItems}
                proposalId={proposalId}
                proposalPrice={latestCreateCall.budget}
                toolCallId={latestCreateCall.toolCallId}
                externalOutcome={externalCommitOutcome(latestCreateCall)}
                platformNameById={platformNameById}
                onResolve={resolveToolOutput(
                  helpers,
                  'createLineItems',
                  latestCreateCall.toolCallId,
                  'Failed to create line items'
                )}
              />
            </div>
          ) : null}
          {latestMutateCall ? (
            <div className={commitPending && !mutatePending ? dimClass : undefined}>
              {latestMutateCall.tool === 'updateLineItems' ? (
                <LineItemBatchUpdateCard
                  key={latestMutateCall.toolCallId}
                  toolCallId={latestMutateCall.toolCallId}
                  input={latestMutateCall.input as UpdateLineItemsToolInput}
                  proposalId={proposalId}
                  externalOutcome={externalCommitOutcome(latestMutateCall)}
                  onEditLineItem={
                    onEditLineItem
                      ? id => {
                          helpers.onClose();
                          onEditLineItem(id);
                        }
                      : undefined
                  }
                  onResolve={resolveToolOutput(
                    helpers,
                    'updateLineItems',
                    latestMutateCall.toolCallId,
                    'Failed to apply the changes'
                  )}
                />
              ) : (
                <LineItemMutateCard
                  key={latestMutateCall.toolCallId}
                  tool={latestMutateCall.tool}
                  input={
                    latestMutateCall.input as DeleteLineItemToolInput | PromoteProposalToolInput
                  }
                  proposalId={proposalId}
                  toolCallId={latestMutateCall.toolCallId}
                  externalOutcome={externalCommitOutcome(latestMutateCall)}
                  onResolve={resolveToolOutput(
                    helpers,
                    latestMutateCall.tool,
                    latestMutateCall.toolCallId,
                    'Failed to apply the change'
                  )}
                />
              )}
            </div>
          ) : null}
          {pending ? (
            <div className={dimClass || undefined}>
              <WorkspaceSuggestions
                id={pending.id}
                question={pending.question}
                options={pending.options}
                multiSelect={pending.multiSelect}
                onSelect={value => {
                  if (value === 'Close') {
                    helpers.onClose();
                    return;
                  }
                  // When the agent offers a task option (e.g. after a successful write), drive the real
                  // task flow — the config card for create, the seeded agent flow for manage tasks —
                  // instead of just echoing the label back as a chat message.
                  const taskId = TASK_LABEL_TO_ID[value];
                  if (taskId) {
                    handleTaskSelect(taskId);
                    return;
                  }
                  helpers.sendMessage(value);
                }}
                disabled={helpers.isBusy || commitPending}
              />
            </div>
          ) : null}
        </div>
      );
    },
    [proposalId, drafts, finalized, channels, inferredFromZoho, mode, onEditLineItem]
  );

  return (
    <>
      <Button onClick={() => setOpen(true)} size="compact">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faWandMagicSparkles} />
          <div>Assist me</div>
        </div>
      </Button>
      {open ? (
        <AiChatModal<LineItemChatMessage>
          open={open}
          onClose={() => setOpen(false)}
          title={modalTitle}
          steps={['Context', 'Configure', 'Review']}
          apiEndpoint="/api/line-item-chat"
          body={{ proposal_id: proposalId }}
          initialMessage="Greet me briefly and show me what I can do for this proposal."
          renderChatContent={renderChatContent}
          renderWorkspaceContent={renderWorkspaceContent}
          commitTools={LINE_ITEM_COMMIT_TOOLS}
          cancelledOutputFor={cancelledWriteResult}
        />
      ) : null}
    </>
  );
}

// The confirmed-write tools whose pending card is a commit moment — the modal's soft lock cancels
// the pending one (with a cancelled BravoWriteResult) when the user keeps typing instead.
const LINE_ITEM_COMMIT_TOOLS = [
  'createLineItems',
  'updateLineItems',
  'deleteLineItem',
  'promoteProposal'
];

// 'superseded' tells the agent this was the modal's soft-cancel — the user's message follows
// immediately, so it should wait for it rather than asking what to change or re-proposing.
function cancelledWriteResult(): BravoWriteResult {
  return { status: 'cancelled', reason: 'superseded' };
}

// Seed message sent to the agent when a (non-create) task is chosen, so it proceeds with that intent.
const LINE_ITEM_TASK_SEEDS: Record<string, string> = {
  edit: 'I want to edit one or more existing line items. Present them as options so I can pick which ones.',
  delete:
    'I want to delete a line item. Present the existing line items as options so I can pick one.',
  promote: 'I want to promote this proposal’s changes to execution.'
};

// Maps the task labels the agent emits via presentOptions after a successful write back to task ids,
// so those buttons drive the same flow as the landing chooser. Labels MUST match lineItemTaskOptions
// and the labels listed in the line-item chat route prompt.
const TASK_LABEL_TO_ID: Record<string, string> = {
  'Create a new line item': 'create',
  'Edit line items': 'edit',
  'Delete a line item': 'delete',
  'Promote changes to execution': 'promote'
};

// Context-derived task options for the landing chooser.
function lineItemTaskOptions(existingCount: number, remainingBudget: number): TaskOption[] {
  const options: TaskOption[] = [
    {
      id: 'create',
      label: 'Create a new line item',
      description:
        remainingBudget > 0.01
          ? `Allocate the remaining $${remainingBudget.toLocaleString()} budget.`
          : 'Add another line item to this proposal.'
    }
  ];
  if (existingCount > 0) {
    options.push(
      {
        id: 'edit',
        label: 'Edit line items',
        description: 'Change one or several at once — name, price, dates, pacing, etc.'
      },
      {
        id: 'delete',
        label: 'Delete a line item',
        description: 'Remove a line item from the proposal.'
      },
      // Promote only makes sense once at least one line item exists — an empty proposal has nothing
      // pending to push. Offering it on an empty proposal dead-ends the chooser (it's dismissed on
      // selection, and the agent has nothing to promote so renders no follow-up options).
      {
        id: 'promote',
        label: 'Promote changes to execution',
        description: 'Push this proposal’s pending changes live.'
      }
    );
  }
  return options;
}

function ZohoDefaultsHydrator({
  campaignContext,
  channels,
  setDrafts,
  setInferredFromZoho,
  zohoHydratedFor
}: {
  campaignContext: CampaignContextData;
  channels: ReturnType<typeof useChannelRatesContext>['channels'];
  setDrafts: React.Dispatch<React.SetStateAction<CardSelection[]>>;
  setInferredFromZoho: React.Dispatch<React.SetStateAction<InferredFromZohoFields>>;
  zohoHydratedFor: React.MutableRefObject<string | null>;
}) {
  const canonicalIds = campaignContext.zoho_intent.canonical_platform_ids;
  const key = `${campaignContext.campaign_name}|${canonicalIds.join(',')}`;

  React.useEffect(() => {
    if (zohoHydratedFor.current === key) return;
    zohoHydratedFor.current = key;

    const { defaults } = resolveZohoDefaults(canonicalIds, channels);

    if (
      defaults.channelId === undefined &&
      defaults.tacticId === undefined &&
      defaults.priceTypeId === undefined &&
      !defaults.platformIds?.length
    ) {
      return;
    }

    // Hydrate the FIRST draft only (preserving its price and any other drafts the user added).
    setDrafts(prev => {
      const first = prev[0] ?? { platformIds: [] };
      const tacticId = defaults.tacticId ?? first.tacticId;
      const priceTypeId = defaults.priceTypeId ?? first.priceTypeId;
      const hydrated: CardSelection = {
        channelId: defaults.channelId ?? first.channelId,
        tacticId,
        priceTypeId,
        platformIds: defaults.platformIds ?? first.platformIds,
        price: first.price,
        // A typed unit price belongs to its tactic + price type; a hydrated change falls back to
        // the new rate-card price.
        unitPrice:
          tacticId === first.tacticId && priceTypeId === first.priceTypeId
            ? first.unitPrice
            : undefined
      };
      return [hydrated, ...prev.slice(1)];
    });

    setInferredFromZoho({
      channel: defaults.channelId !== undefined,
      tactic: defaults.tacticId !== undefined,
      priceType: defaults.priceTypeId !== undefined,
      platforms: !!defaults.platformIds?.length
    });
  }, [key, canonicalIds, channels, setDrafts, setInferredFromZoho, zohoHydratedFor]);

  return null;
}

function clearOverriddenBadges(
  prev: InferredFromZohoFields,
  prevSelection: CardSelection,
  nextSelection: CardSelection
): InferredFromZohoFields {
  const next = { ...prev };
  if (nextSelection.channelId !== prevSelection.channelId) next.channel = false;
  if (nextSelection.tacticId !== prevSelection.tacticId) next.tactic = false;
  if (nextSelection.priceTypeId !== prevSelection.priceTypeId) next.priceType = false;
  const samePlatforms =
    [...prevSelection.platformIds].sort().join(',') ===
    [...nextSelection.platformIds].sort().join(',');
  if (!samePlatforms) next.platforms = false;
  return next;
}

// The Configure step the agent advances to (updateStep) when the user should use the config card.
const CONFIGURE_STEP = 2;

// Latest step the agent advanced to (from data-step-update parts), or null. Used to show the config
// card whenever the agent directs the user to it — see the harness-guarantee note at the call site.
function latestStep(messages: LineItemChatMessage[]): number | null {
  let step: number | null = null;
  for (const m of messages) {
    if (m.role !== 'assistant') continue;
    for (const part of m.parts) {
      if (part.type === 'data-step-update') {
        step = (part.data as StepUpdateData).step;
      }
    }
  }
  return step;
}

// The most recent prefillLineItems data part (its part id + the drafts the agent sent), or null.
function latestPrefillLineItems(
  messages: LineItemChatMessage[]
): { id: string; data: PrefillLineItemsData } | null {
  let result: { id: string; data: PrefillLineItemsData } | null = null;
  for (const m of messages) {
    if (m.role !== 'assistant') continue;
    for (const part of m.parts) {
      if (part.type === 'data-prefill-line-items') {
        result = {
          id: 'id' in part && typeof part.id === 'string' ? part.id : 'prefill',
          data: part.data as PrefillLineItemsData
        };
      }
    }
  }
  return result;
}

// Applies an agent prefillLineItems part to the configuration drafts (once per distinct part) and
// enters create mode so the pre-filled card(s) show. The user then reviews/edits and finalizes.
function PrefillHydrator({
  prefill,
  channels,
  setDrafts,
  setMode,
  setInferredFromZoho,
  prefillAppliedFor
}: {
  prefill: { id: string; data: PrefillLineItemsData };
  channels: Channel[];
  setDrafts: React.Dispatch<React.SetStateAction<CardSelection[]>>;
  setMode: React.Dispatch<React.SetStateAction<'create' | 'agent' | null>>;
  setInferredFromZoho: React.Dispatch<React.SetStateAction<InferredFromZohoFields>>;
  prefillAppliedFor: React.MutableRefObject<string | null>;
}) {
  React.useEffect(() => {
    if (prefillAppliedFor.current === prefill.id) return;
    prefillAppliedFor.current = prefill.id;
    setDrafts(prev => prefillDrafts(prev, prefill.data.line_items, channels));
    setInferredFromZoho({});
    setMode('create');
  }, [prefill, channels, setDrafts, setMode, setInferredFromZoho, prefillAppliedFor]);

  return null;
}

// The onResolve handler every confirm card shares: report a failed write as a tool error so the
// agent can explain and re-propose; report anything else as the tool's output.
function resolveToolOutput(
  helpers: WorkspaceHelpers,
  tool: keyof LineItemChatTools,
  toolCallId: string,
  fallbackError: string
) {
  return (output: BravoWriteResult) =>
    output.status === 'failed'
      ? helpers.addToolOutput({
          tool,
          toolCallId,
          state: 'output-error',
          errorText: output.error ?? fallbackError
        })
      : helpers.addToolOutput({ tool, toolCallId, state: 'output-available', output });
}
